import { Dispatch, SetStateAction, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	useContentListQuery,
	useContentListStateHandler,
	ContentListAction,
} from '@_features/content';

import { VerticalArrowsIcon } from '@_shared/icons';
import { CHARACTER_IMGS } from '@_shared/imgs';
import { SkeletonUI } from '@_shared/ui';
import { Pagination } from '@_shared/ui/Pagination';
import { Typography } from '@_shared/ui/Typography';

import { SkeletonContentListItem, ContentListItem } from '../ContentListItem';
import * as S from './ContentList.style';
import { SearchBar } from './SearchBar';

interface ContentListProps {
	isAllFolderPage: boolean;
	isEditMode: boolean;
	setIsEditMode: Dispatch<SetStateAction<boolean>>;
	selectedContentIds: number[];
	dispatch: Dispatch<ContentListAction>;
}

export function ContentList({
	isAllFolderPage,
	isEditMode,
	setIsEditMode,
	selectedContentIds,
	dispatch,
}: ContentListProps) {
	const { id } = useParams();
	const navigate = useNavigate();
	const searchBarRef = useRef<HTMLInputElement>(null);
	const {
		orderState: [order, setOrder],
		keywordState: [keyword, setKeyword],
		isContentListLoading,
		isSearching,
		isSearchData,
		isContentListEmpty,
		isSearchDataEmpty,
	} = useContentListStateHandler(Number(id));

	const { data, isLoading, hasNextPage, fetchNextPage } = useContentListQuery(
		id as string,
		order,
		keyword,
	);

	function isContentSelected(contentId: number) {
		return selectedContentIds.includes(contentId);
	}

	function handleOnSearchSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		const formData = new FormData(e.currentTarget);
		setKeyword(formData.get('search') as string);
	}

	if (isContentListLoading(isLoading)) {
		return (
			// {/* @TODO: 로딩/fallback 이 있는 Suspense 같은 형태의 UI 로 관심사 분리 가능할것 같은데 */}
			<S.Container>
				<S.Header isAllFolderPage={isAllFolderPage}>
					<SkeletonUI width="50px" height="24px" borderRadius="8px" />
					<SkeletonUI width="32px" height="24px" borderRadius="8px" />
				</S.Header>

				{/* List Search Bar */}
				<S.SearchBarWrapper>
					<SkeletonUI width="100%" height="48px" borderRadius="8px" />
				</S.SearchBarWrapper>

				{/* order button */}
				<SkeletonUI width="100%" height="24px" borderRadius="8px" />

				<SkeletonContentListItem />
				<SkeletonContentListItem />
				<SkeletonContentListItem />
				<SkeletonContentListItem />
			</S.Container>
		);
	}

	if (isContentListEmpty(data?.count)) {
		return (
			<S.NoDataContainer>
				<S.NoDataImg src={CHARACTER_IMGS.HALF_BODY_MAGNIFYING_GLASS_CHARACTER_IMG} />
				<Typography type="headline-small" color="brown-primary" textAlign="center">
					딸깍에 첫 콘텐츠를 담아보세요
				</Typography>
				<Typography
					type="title-medium"
					color="orange-primary"
					underline
					textAlign="center"
					onClick={() => {
						navigate('/onboarding');
					}}
				>
					딸깍 사용법이 궁금하다면?
				</Typography>
			</S.NoDataContainer>
		);
	}

	return (
		<Pagination
			onPaginate={() => {
				if (hasNextPage) {
					fetchNextPage();
				}
			}}
		>
			<S.Container>
				{/* List Header */}
				<S.Header isAllFolderPage={isAllFolderPage}>
					<Typography type="title-medium" color="font-primary">
						{(() => {
							if (isEditMode) {
								return (
									<>
										선택{' '}
										<S.FontHighlight>
											{selectedContentIds.length}
										</S.FontHighlight>
									</>
								);
							}
							if (isSearchData) {
								return (
									<>
										검색결과 <S.FontHighlight>{data?.count}</S.FontHighlight>
									</>
								);
							}
							return (
								<>
									전체 <S.FontHighlight>{data?.count}</S.FontHighlight>
								</>
							);
						})()}
					</Typography>

					{isEditMode ? (
						<S.EditModeButton>
							<Typography
								type="title-small"
								color="orange-primary"
								onClick={() => {
									dispatch({ type: 'CLEAR_SELECTED_CONTENT' });
									setIsEditMode(false);
								}}
							>
								취소
							</Typography>
						</S.EditModeButton>
					) : (
						<Typography
							type="title-medium"
							color="font-primary"
							onClick={() => {
								setIsEditMode((prev) => !prev);
							}}
						>
							<S.FontHighlight>편집</S.FontHighlight>
						</Typography>
					)}
				</S.Header>

				{/* List Search Bar */}
				{!isEditMode && (
					<S.SearchBarWrapper>
						<SearchBar
							initialKeyword={keyword}
							onSubmit={handleOnSearchSubmit}
							isSearchData={isSearchData}
							ref={searchBarRef}
							onClear={() => {
								if (searchBarRef.current) {
									searchBarRef.current.value = '';
								}
								setKeyword('');
							}}
						/>
					</S.SearchBarWrapper>
				)}

				{isSearching(isLoading) ? (
					<S.SearchContainer>
						<img src={CHARACTER_IMGS.SLEEPING_CHARACTER_GIF} alt="sleeping_character" />
						<Typography type="title-large" color="gray-primary">
							검색 결과를 찾고 있어요
						</Typography>
					</S.SearchContainer>
				) : (
					<>
						{/* Ordered Button */}
						<S.OrderedButtonContainer>
							<S.OrderedButton
								onClick={() => {
									setOrder(order === 'latest' ? 'oldest' : 'latest');
								}}
							>
								<Typography type="label-large" color="font-secondary">
									{order === 'latest' ? '최신순' : '과거순'}
								</Typography>
								<VerticalArrowsIcon />
							</S.OrderedButton>
						</S.OrderedButtonContainer>

						{isSearchDataEmpty(data?.count ?? 0) ? (
							<S.NoSearchDataContainer>
								<S.NoSearchDataImg src={CHARACTER_IMGS.ANGRY_CHARACTER_IMG} />
								<Typography type="title-large" color="gray-primary">
									검색 결과가 없어요!
								</Typography>
								<Typography
									type="body-large"
									color="font-tertiary"
									textAlign="center"
								>
									키워드를 정확하게 입력하셨는지
									<br />
									확인해보세요.
								</Typography>
							</S.NoSearchDataContainer>
						) : (
							<>
								{data?.pinnedData?.map(
									(content) =>
										content !== undefined && (
											<ContentListItem
												isAllFolderPage={isAllFolderPage}
												key={content.id}
												data={content}
												isPinned
												isEditMode={isEditMode}
												isContentSelected={isContentSelected}
												dispatch={dispatch}
											/>
										),
								)}
								{data?.contentData.map((content) => (
									<ContentListItem
										isAllFolderPage={isAllFolderPage}
										key={content.id}
										data={content}
										isPinned={false}
										isEditMode={isEditMode}
										isContentSelected={isContentSelected}
										dispatch={dispatch}
									/>
								))}
							</>
						)}
					</>
				)}
			</S.Container>
		</Pagination>
	);
}
